// Recording orchestrator: mixer frames → chunker → WAV → upload
// queue with retry/backoff. Failure policy:
// docs/design-docs/audio-capture.md (§ Failure behavior)

import { ApiError, uploadChunk } from "../lib/api";
import { rmsOf } from "./vad";
import { CaptureMixer } from "./mixer";
import { wavBlob } from "./wav";

// Messages from the VAD worker (capture/vad-worker.ts).
type WorkerMsg =
  | { type: "ready"; classifier: "silero" | "energy" }
  | { type: "chunk"; pcm: Float32Array; startOffsetMs: number; durationMs: number; speechMs: number }
  | { type: "flushed" };

export interface RecorderEvents {
  onLevel: (rms: number) => void;
  onQueueDepth: (depth: number) => void;
  onSystemEnded: () => void;
  onUploadError: (message: string) => void;
}

interface QueuedChunk {
  seq: number;
  startOffsetMs: number;
  wav: Blob;
  attempts: number;
}

const MAX_QUEUE = 50;
const MAX_ATTEMPTS = 4;

export class Recorder {
  private mixer = new CaptureMixer();
  private worker: Worker | null = null;
  private flushResolve: (() => void) | null = null;
  private queue: QueuedChunk[] = [];
  private seq = 0;
  private uploading = false;
  private running = false;
  private paused = false;

  constructor(
    private meetingId: number,
    private events: RecorderEvents,
    /// Guest token for hosted meetings (D14).
    private gt?: string | null,
    /// Meeting-time anchor: added to the recorder's own sample clock
    /// so chunks from every participant land on ONE shared timeline,
    /// regardless of when each joined. Server-issued (client clocks
    /// can't be trusted); 0 for solo recordings.
    private baseOffsetMs = 0,
  ) {}

  async start(micDeviceId?: string): Promise<void> {
    this.running = true;
    this.startWorker();
    await this.mixer.start(micDeviceId, {
      onFrame: (frame) => this.handleFrame(frame),
      onSystemEnded: () => this.events.onSystemEnded(),
    });
  }

  /// Hosted calls: tap the mic stream LiveKit is already publishing
  /// (one capture pipeline, exact same audio the others hear).
  async startFromStream(stream: MediaStream): Promise<void> {
    this.running = true;
    this.startWorker();
    await this.mixer.startWithStream(stream, {
      onFrame: (frame) => this.handleFrame(frame),
      onSystemEnded: () => this.events.onSystemEnded(),
    });
  }

  /// Spin up the VAD worker (Silero w/ energy fallback). Chunk assembly
  /// lives there; we just forward frames and upload what comes back.
  private startWorker(): void {
    this.worker = new Worker(new URL("./vad-worker.ts", import.meta.url), {
      type: "module",
    });
    this.worker.onmessage = (e: MessageEvent<WorkerMsg>) => this.onWorkerMessage(e.data);
    this.worker.onerror = (e) =>
      this.events.onUploadError(`VAD worker error: ${e.message}`);
    // Verbose per-frame VAD logging is opt-in: localStorage mm-vad-debug=1
    // or a ?vadDebug query param (workers can't read either directly).
    let debug = false;
    try {
      debug =
        localStorage.getItem("mm-vad-debug") === "1" ||
        new URLSearchParams(location.search).has("vadDebug");
    } catch {
      /* storage blocked — leave debug off */
    }
    this.worker.postMessage({ type: "init", debug });
  }

  private onWorkerMessage(m: WorkerMsg): void {
    if (m.type === "ready") {
      console.log(
        `%c[recorder]%c VAD worker ready — classifier=${m.classifier}`,
        "color:#0ea5e9;font-weight:bold",
        "color:inherit",
      );
    } else if (m.type === "chunk") {
      this.enqueue(m.pcm, m.startOffsetMs);
    } else if (m.type === "flushed") {
      this.flushResolve?.();
      this.flushResolve = null;
    }
  }

  /// Muted (self or by host): drop frames so silence isn't chunked
  /// and mute never bills STT seconds.
  setPaused(paused: boolean): void {
    this.paused = paused;
  }

  async addSystemAudio(): Promise<boolean> {
    return this.mixer.addSystemAudio();
  }

  hasSystemAudio(): boolean {
    return this.mixer.hasSystemAudio();
  }

  /** Stop capture, flush the open chunk, drain the queue. */
  async stop(): Promise<void> {
    this.running = false;
    await this.mixer.stop();
    // Ask the worker to close the open chunk, then wait for it (bounded:
    // a stuck worker must not hang the stop path).
    await this.flushWorker();
    this.worker?.terminate();
    this.worker = null;
    await this.drain();
  }

  private flushWorker(): Promise<void> {
    if (!this.worker) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.flushResolve = resolve;
      this.worker!.postMessage({ type: "flush" });
      setTimeout(() => {
        if (this.flushResolve) {
          this.flushResolve = null;
          resolve();
        }
      }, 2_000);
    });
  }

  private handleFrame(frame: Float32Array): void {
    if (!this.running || this.paused || !this.worker) return;
    // Meter reads the frame first; then hand the buffer to the worker
    // (transfer = zero-copy; nothing on this thread reuses it).
    this.events.onLevel(rmsOf(frame));
    this.worker.postMessage({ type: "frame", frame }, [frame.buffer]);
  }

  private enqueue(pcm: Float32Array, startOffsetMs: number): void {
    if (this.queue.length >= MAX_QUEUE) {
      // Bounded queue (≈16 min of speech): drop oldest, warn loudly.
      this.queue.shift();
      this.events.onUploadError("upload queue overflow — dropping oldest chunk");
    }
    const base = Number.isFinite(this.baseOffsetMs) ? this.baseOffsetMs : 0;
    const offset = startOffsetMs + base;
    this.queue.push({
      seq: this.seq++,
      startOffsetMs: Number.isFinite(offset) ? offset : 0,
      wav: wavBlob(pcm),
      attempts: 0,
    });
    this.events.onQueueDepth(this.queue.length);
    void this.pump();
  }

  /** Serial upload pump — chunk order keeps STT prompt-chaining sane. */
  private async pump(): Promise<void> {
    if (this.uploading) return;
    this.uploading = true;
    try {
      while (this.queue.length > 0) {
        const item = this.queue[0]!;
        try {
          await uploadChunk(this.meetingId, item.seq, item.startOffsetMs, item.wav, this.gt);
          this.queue.shift();
          this.events.onQueueDepth(this.queue.length);
        } catch (e) {
          item.attempts += 1;
          const rateLimited = e instanceof ApiError && e.status === 429;
          const fatal =
            e instanceof ApiError && (e.status === 402 || e.status === 409);
          if (fatal) {
            // Budget exceeded / meeting closed: stop trying.
            this.events.onUploadError(e.message);
            this.queue.length = 0;
            this.events.onQueueDepth(0);
            return;
          }
          if (item.attempts >= MAX_ATTEMPTS) {
            this.queue.shift();
            this.events.onUploadError(
              e instanceof Error ? e.message : "chunk upload failed",
            );
            continue;
          }
          // Backoff: 2^attempts seconds, longer when rate limited.
          const delay = (rateLimited ? 5_000 : 1_000) * 2 ** (item.attempts - 1);
          await new Promise((r) => setTimeout(r, Math.min(delay, 30_000)));
        }
      }
    } finally {
      this.uploading = false;
    }
  }

  private async drain(): Promise<void> {
    // pump() exits when empty; poll until it has drained or given up.
    while (this.queue.length > 0) {
      await this.pump();
      if (this.queue.length > 0) {
        await new Promise((r) => setTimeout(r, 250));
      }
    }
  }
}
